import { DrawerPanel } from '@dmc/ui';
import { ModelName } from '@casops/store/api';
import { useRenderKey } from '@casops/hooks/useRenderKey';
import UserForm from './components/user-form/UserForm';
import useLoadModelData from '@casops/components/layout/model-form/useLoadModelData';
import { useGetUserByIdQuery } from '@casops/store/apis/usersApi';
import { ReactElement, useCallback } from 'react';
import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';
import { useModelFormTitle } from '@casops/components/layout/model-form/useModelFormTitle';
import { useGetRolesQuery, UserRole, UserRoleModel } from '@casops/store/apis/settingsApi';
import { useTranslation } from 'react-i18next';

const modelName = ModelName.User;

export function ManageUser(): ReactElement {
  const { t } = useTranslation();
  const title = useModelFormTitle(modelName);

  const { isLoaded: isItemLoaded, item } = useLoadModelData({
    useModelQueryHook: useGetUserByIdQuery,
  });

  const { currentData: roles, isSuccess: isSuccessRoles } = useGetRolesQuery();

  // Combine the loaded state of the main data model with any additional data needed for the form.
  const isLoaded = [isItemLoaded, roles, isSuccessRoles].every(Boolean);

  const getRoleOptions = useCallback((): DropdownItemProps<UserRole>[] => {
    if (roles) {
      return roles
        .map(({ id, assignable }: UserRoleModel) => ({
          key: id,
          value: id,
          stringValue: id,
          label: t(`UserRole.${id}`),
          disabled: !assignable,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    return [];
  }, [t, roles]);

  // Base the renderKey on all data that can change, including roles.
  const renderKey = useRenderKey({ item, roles });

  return (
    <DrawerPanel isLoaded={isLoaded} header={title}>
      <UserForm key={renderKey} item={item} getRoleOptions={getRoleOptions} />
    </DrawerPanel>
  );
}

export default ManageUser;
